import { useMutation, useQuery, useQueryClient } from '../useQuery'
import { queryKeys } from '@/api/queryKeys'
import * as systemApi from '@/api/system'
import type {
  EmployeeEmploymentStatus,
  UpsertSystemEmployeeRequest,
  UpsertSystemPermissionRequest,
  UpsertSystemRoleRequest,
} from '@/api/types/system'
import type {
  UpsertSystemDictionaryItemRequest,
  UpsertSystemDictionaryRequest,
} from '@/api/types/dictionary'

function invalidateSystemData(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['system'] })
}

export function useSystemEmployees(params: { keyword?: string; status?: EmployeeEmploymentStatus } = {}) {
  return useQuery({
    queryKey: queryKeys.systemEmployees(params),
    queryFn: () => systemApi.getSystemEmployees(params),
  })
}

export function useCreateSystemEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertSystemEmployeeRequest) => systemApi.createSystemEmployee(data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useUpdateSystemEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpsertSystemEmployeeRequest }) =>
      systemApi.updateSystemEmployee(id, data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useReplaceEmployeeRoles() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, roleIds }: { id: number; roleIds: number[] }) =>
      systemApi.replaceEmployeeRoles(id, roleIds),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useSystemRoles() {
  return useQuery({ queryKey: queryKeys.systemRoles, queryFn: systemApi.getSystemRoles })
}

export function useCreateSystemRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertSystemRoleRequest) => systemApi.createSystemRole(data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useUpdateSystemRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpsertSystemRoleRequest }) =>
      systemApi.updateSystemRole(id, data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useReplaceRolePermissions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, permissionIds }: { id: number; permissionIds: number[] }) =>
      systemApi.replaceRolePermissions(id, permissionIds),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useSystemPermissions() {
  return useQuery({ queryKey: queryKeys.systemPermissions, queryFn: systemApi.getSystemPermissions })
}

export function useCreateSystemPermission() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertSystemPermissionRequest) => systemApi.createSystemPermission(data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useUpdateSystemPermission() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpsertSystemPermissionRequest }) =>
      systemApi.updateSystemPermission(id, data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useSystemDictionaries(params: { keyword?: string; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.systemDictionaries(params),
    queryFn: () => systemApi.getSystemDictionaries(params),
  })
}

export function useCreateSystemDictionary() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertSystemDictionaryRequest) => systemApi.createSystemDictionary(data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useUpdateSystemDictionary() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpsertSystemDictionaryRequest }) =>
      systemApi.updateSystemDictionary(id, data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useSystemDictionaryItems(dictionaryId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.systemDictionaryItems(dictionaryId ?? 0),
    queryFn: () => systemApi.getSystemDictionaryItems(dictionaryId!),
    enabled: dictionaryId !== undefined,
  })
}

export function useCreateSystemDictionaryItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ dictionaryId, data }: { dictionaryId: number; data: UpsertSystemDictionaryItemRequest }) =>
      systemApi.createSystemDictionaryItem(dictionaryId, data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}

export function useUpdateSystemDictionaryItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ dictionaryId, itemId, data }: { dictionaryId: number; itemId: number; data: UpsertSystemDictionaryItemRequest }) =>
      systemApi.updateSystemDictionaryItem(dictionaryId, itemId, data),
    onSuccess: () => invalidateSystemData(queryClient),
  })
}
